import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Phone } from "lucide-react";
import SEO from "@/components/SEO";
import NotFound from "@/pages/NotFound";
import { api } from "@/lib/auth";

export default function CoveragePage() {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (typeof window !== "undefined") window.prerenderReady = false;
        setLoading(true);
        setNotFound(false);
        api
            .get(`/public/coverage/${slug}`)
            .then((r) => setPage(r.data))
            .catch((err) => {
                if (err?.response?.status === 404) setNotFound(true);
            })
            .finally(() => {
                setLoading(false);
                requestAnimationFrame(() => {
                    if (typeof window !== "undefined") window.prerenderReady = true;
                });
            });
    }, [slug]);

    if (notFound) return <NotFound />;
    if (loading || !page)
        return (
            <div className="py-24 text-center text-sm text-mm-muted" data-testid="coverage-loading">
                Loading...
            </div>
        );

    const brandPhone = page.brand?.phone || "(813) 739-9877";
    const brandPhoneTel = page.brand?.phone_tel || "+18137399877";
    const jsonLdArray = [page.service_ld, page.breadcrumb_ld, page.faq_ld].filter(Boolean);

    return (
        <div className="bg-mm-bg/40" data-testid={`coverage-page-${page.slug}`}>
            <SEO
                title={page.meta_title}
                description={page.meta_description}
                canonical={page.canonical}
            />
            {jsonLdArray.map((ld, i) => (
                <JsonLdScript key={i} id={`cov-ld-${i}`} data={ld} />
            ))}

            <section
                className="border-b border-mm-line bg-gradient-to-b from-mm-bg to-white py-14"
                data-testid={`coverage-hero-${page.slug}`}
            >
                <div className="mm-container">
                    <div className="text-[0.72rem] uppercase tracking-wider text-mm-blue">
                        Coverage
                    </div>
                    <h1
                        className="mt-3 max-w-2xl font-serif text-3xl text-mm-navy sm:text-5xl"
                        data-testid="coverage-title"
                    >
                        {page.title}
                    </h1>
                    <p className="mt-4 max-w-2xl text-lg text-mm-ink">{page.lede}</p>
                </div>
            </section>

            <div className="mm-container py-12">
                <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
                    <div data-testid="coverage-prose">
                        {page.paragraphs.map((p, i) => (
                            <p key={i} className="mb-4 max-w-2xl text-base text-mm-ink">
                                {p}
                            </p>
                        ))}

                        <div
                            className="mt-6 rounded-2xl border border-mm-line bg-white p-6"
                            data-testid="coverage-who-it-fits"
                        >
                            <h2 className="font-serif text-xl text-mm-navy">
                                Who it typically fits
                            </h2>
                            <ul className="mt-3 list-disc pl-5">
                                {page.who_it_fits.map((w, i) => (
                                    <li key={i} className="my-1.5 text-mm-ink">
                                        {w}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {page.faq && page.faq.length > 0 && (
                            <section className="mt-10" data-testid="coverage-faq">
                                <h2 className="mb-3 font-serif text-2xl text-mm-navy">
                                    Frequently asked questions
                                </h2>
                                <div className="space-y-2">
                                    {page.faq.map((item, i) => (
                                        <details
                                            key={i}
                                            className="overflow-hidden rounded-xl border border-mm-line bg-white"
                                            data-testid={`coverage-faq-item-${i + 1}`}
                                        >
                                            <summary className="flex cursor-pointer items-center justify-between px-5 py-4 font-semibold text-mm-navy">
                                                <span>{item.q}</span>
                                                <span className="text-mm-blue">+</span>
                                            </summary>
                                            <div className="px-5 pb-5 text-sm text-mm-ink">
                                                {item.a}
                                            </div>
                                        </details>
                                    ))}
                                </div>
                            </section>
                        )}

                        {page.tpmo_disclaimer && (
                            <div
                                className="mt-8 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                                data-testid="coverage-tpmo"
                            >
                                <strong>Important Medicare disclaimer: </strong>
                                {page.tpmo_disclaimer} We are not affiliated with or
                                endorsed by the United States government or the federal
                                Medicare program.
                            </div>
                        )}

                        <p className="mt-8 rounded-xl border border-dashed border-mm-line bg-white p-4 text-sm italic text-mm-muted">
                            Moving Mountains Advisors does not guarantee approval,
                            rates, or outcomes. Insurance product features vary by
                            carrier, state, age, and health. All examples above are
                            educational and not an offer of coverage.
                        </p>
                    </div>

                    <aside
                        className="self-start rounded-2xl border border-mm-line bg-white p-6 lg:sticky lg:top-24"
                        data-testid="coverage-side-cta"
                    >
                        <h3 className="font-semibold text-mm-navy">Talk through your fit</h3>
                        <div className="mt-3 flex flex-col gap-2">
                            <Link
                                to="/jarvis"
                                className="inline-flex items-center justify-center gap-1 rounded-full bg-mm-navy px-4 py-2 text-sm font-semibold text-white hover:bg-mm-blue"
                                data-testid="coverage-cta-jarvis"
                            >
                                Talk to Jarvis
                            </Link>
                            <Link
                                to="/book"
                                className="inline-flex items-center justify-center gap-1 rounded-full border border-mm-line px-4 py-2 text-sm font-semibold text-mm-navy hover:border-mm-navy"
                                data-testid="coverage-cta-book"
                            >
                                Book a 20-minute call
                            </Link>
                            <a
                                href={`tel:${brandPhoneTel}`}
                                className="inline-flex items-center justify-center gap-1 rounded-full border border-mm-line px-4 py-2 text-sm font-semibold text-mm-navy hover:border-mm-navy"
                                data-testid="coverage-cta-phone"
                            >
                                <Phone size={14} /> {brandPhone}
                            </a>
                        </div>
                        <p className="mt-4 text-xs text-mm-muted">
                            Independent, licensed advisors. We do not sell your data.
                        </p>
                    </aside>
                </div>

                {page.related && page.related.length > 0 && (
                    <section className="mt-14" data-testid="coverage-related">
                        <h2 className="mb-4 font-serif text-2xl text-mm-navy">
                            {page.related_header || "Related reading"}
                        </h2>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {page.related.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/blog/${r.slug}`}
                                    className="overflow-hidden rounded-2xl border border-mm-line bg-white transition-colors hover:border-mm-sky"
                                    data-testid={`coverage-related-${r.slug}`}
                                >
                                    <div className="aspect-video overflow-hidden bg-mm-bg">
                                        {r.hero_image_url && (
                                            <img
                                                src={r.hero_image_url}
                                                alt={r.hero_image_alt || r.title}
                                                loading="lazy"
                                                className="h-full w-full object-cover"
                                            />
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <span className="text-[0.7rem] uppercase tracking-wider text-mm-blue">
                                            {r.category}
                                        </span>
                                        <h3 className="mt-1 text-sm font-semibold text-mm-navy">
                                            {r.title}
                                        </h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

function JsonLdScript({ id, data }) {
    useEffect(() => {
        if (!data) return;
        let el = document.getElementById(id);
        if (!el) {
            el = document.createElement("script");
            el.type = "application/ld+json";
            el.id = id;
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(data);
        return () => {
            const n = document.getElementById(id);
            if (n && n.parentNode) n.parentNode.removeChild(n);
        };
    }, [id, data]);
    return null;
}
